/* Doodhvale Farms product card behaviour. Markup: snippets/dvf-card.liquid.
   - <dvf-card> sets itself up when it enters the page, so cards swapped in by
     filtering / sorting / pagination work without re-running anything.
   - Size picker, add to cart and the toast use one listener each on the document. */
(() => {
  if (window.DVFCard) return;

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const HOUR = 3600000;
  const ICON_OK = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

  /* Same wording as the first paint worked out in sections/main-collection.liquid */
  const timeLeft = (ms) => {
    const m = Math.floor(ms / 60000), d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60);
    if (d >= 2) return d + ' days';
    if (d === 1) return '1 day' + (h ? ' ' + h + (h === 1 ? ' hr' : ' hrs') : '');
    if (h >= 1) return h + (h === 1 ? ' hour' : ' hours');
    return Math.max(m, 1) + ' min';
  };

  /* ---------- Offer countdown: one shared timer for every card on the page ---------- */
  const deals = new Set();
  let timer = 0;
  const tick = () => {
    const now = Date.now();
    deals.forEach((card) => card.updateDeal(now));
    if (!deals.size) { clearInterval(timer); timer = 0; }
  };

  /* ---------- Hover image: only fetched on devices with a mouse, near the viewport ---------- */
  const finePointer = window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches;
  const loadHover = (media) => {
    const d = media.dataset;
    if (!d.hoverSrc) return;
    const img = new Image();
    img.className = 'dvf-card__hover';
    img.alt = '';
    img.decoding = 'async';
    img.onload = () => img.classList.add('is-loaded');
    img.sizes = d.sizes || '';
    img.srcset = d.hoverSrcset || '';
    img.src = d.hoverSrc;
    delete d.hoverSrc;
    media.querySelector('img').after(img);
  };
  const hoverObserver = finePointer && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((e) => {
        if (!e.isIntersecting) return;
        hoverObserver.unobserve(e.target);
        loadHover(e.target);
      }), { rootMargin: '200px' })
    : null;

  class DvfCard extends HTMLElement {
    connectedCallback() {
      const deal = this.querySelector('.dvf-card__deal');
      if (deal) {
        this.dealEl = deal;
        this.dealEnd = Date.parse(deal.dataset.ends);
        if (isNaN(this.dealEnd)) this.endDeal();
        else {
          deals.add(this);
          this.updateDeal(Date.now());
          if (!timer && deals.size) timer = setInterval(tick, 30000);
        }
      }
      const media = this.querySelector('.dvf-card__media[data-hover-src]');
      if (media && hoverObserver && media.querySelector('img')) hoverObserver.observe(media);
    }

    disconnectedCallback() {
      deals.delete(this);
      const media = this.querySelector('.dvf-card__media');
      if (media && hoverObserver) hoverObserver.unobserve(media);
    }

    updateDeal(now) {
      const left = this.dealEnd - now;
      if (left <= 0) return this.endDeal();
      const tpl = this.dealEl.dataset.tpl || '';
      if (tpl.includes('{time}')) {
        this.dealEl.querySelector('.dvf-card__deal-text').textContent = tpl.replace('{time}', timeLeft(left));
      }
      this.dealEl.classList.toggle('is-urgent', left < 6 * HOUR);
    }

    /* Offer is over: CSS swaps the countdown back to "You save …" */
    endDeal() {
      deals.delete(this);
      this.classList.remove('dvf-card--deal');
    }

    /* Size picker: every option carries its own pre-formatted prices */
    selectVariant(option) {
      const d = option.dataset;
      const off = +d.off || 0;
      this.querySelector('.dvf-card__now').textContent = d.price;
      this.querySelector('.dvf-card__was').textContent = d.was || '';
      this.querySelector('.dvf-card__save').textContent = d.save || '';
      this.querySelector('.dvf-card__off').textContent = off ? off + '% off' : '';
      this.classList.toggle('dvf-card--no-discount', !off);
      if (d.url) this.querySelectorAll('.dvf-card__media, .dvf-card__title').forEach((a) => { a.href = d.url; });

      const btn = this.querySelector('.dvf-card__btn');
      const soldOut = option.hasAttribute('data-soldout');
      btn.disabled = soldOut;
      btn.querySelector('span').textContent = soldOut ? 'Sold out' : btn.dataset.label;

      const batch = this.querySelector('.dvf-card__batch');
      if (batch && d.left != null) this.updateBatch(batch, +d.left || 0);
    }

    /* "N left in this batch" for the chosen size; 0 = that size shows no meter */
    updateBatch(el, left) {
      el.hidden = !left;
      if (!left) return;
      const size = +el.dataset.batch || 100;
      el.classList.toggle('is-low', left <= +el.dataset.lowAt);
      el.querySelector('.dvf-card__batch-text').textContent = el.dataset.tpl.replace('{count}', left);
      el.querySelector('.dvf-card__batch-bar b').style.width = Math.max(4, Math.floor(left * 100 / size)) + '%';
    }
  }
  customElements.define('dvf-card', DvfCard);

  document.addEventListener('change', (e) => {
    const select = e.target.closest && e.target.closest('.dvf-card__variant');
    if (select) select.closest('dvf-card').selectVariant(select.options[select.selectedIndex]);
  });

  /* ---------- Add to cart without leaving the page ---------- */
  const setButton = (btn, state) => {
    if (!btn.dataset.html) btn.dataset.html = btn.innerHTML;
    clearTimeout(btn._reset);
    btn.classList.remove('is-added');
    btn.removeAttribute('aria-busy');
    if (state === 'loading') {
      btn.setAttribute('aria-busy', 'true');
      btn.innerHTML = '<i class="dvf-spin" aria-hidden="true"></i><span>Adding…</span>';
    } else if (state === 'added') {
      btn.classList.add('is-added');
      btn.innerHTML = ICON_OK + '<span>Added</span>';
      btn._reset = setTimeout(() => setButton(btn, ''), 1800);
    } else {
      btn.innerHTML = btn.dataset.html;
      btn.querySelector('span').textContent = btn.disabled ? 'Sold out' : btn.dataset.label;
    }
  };

  const json = (res) => res.json().then((body) => {
    if (!res.ok) throw new Error(body.description || body.message || '');
    return body;
  });

  const addToCart = async (form) => {
    const btn = form.querySelector('.dvf-card__btn');
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    const card = form.closest('dvf-card');
    const drawer = document.querySelector('cart-drawer');
    const openDrawer = card.dataset.after === 'cart' && !!drawer;

    setButton(btn, 'loading');
    const body = new FormData(form);
    body.append('sections', 'mini-cart'); // lets the theme's cart drawer re-render without another request
    try {
      const added = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        body,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      }).then(json);
      const cart = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(json);

      /* The same events the theme fires after its own add to cart: header count, drawer, progress bar */
      const root = document.documentElement;
      root.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      root.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: Object.assign({}, cart, { sections: added.sections }), openMiniCart: openDrawer }
      }));
      setButton(btn, 'added');
      document.dispatchEvent(new CustomEvent('shopflo-event:add-to-cart'));
      if (!openDrawer) showToast(card, false);
    } catch (err) {
      setButton(btn, '');
      const offline = err && err.name === 'TypeError'; // fetch() rejects with a TypeError when the network fails
      showToast(card, true, (!offline && err && err.message) || 'Could not add this item. Please try again.');
    }
  };

  document.addEventListener('submit', (e) => {
    const form = e.target.closest && e.target.closest('.dvf-card__form');
    if (!form) return;
    e.preventDefault();
    addToCart(form);
  });

  /* ---------- Toast ---------- */
  let toast, toastTimer;
  const getToast = () => {
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'dvf-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = '<img alt=""><div class="dvf-toast__txt"><b></b><span></span></div><a href="' + ROOT + 'cart">View cart</a>';
    document.body.appendChild(toast);
    return toast;
  };

  /* Keep the toast above any fixed bar along the bottom of the screen (e.g. a sticky checkout bar) */
  const bottomBarHeight = () => {
    if (!document.elementsFromPoint) return 0;
    const h = innerHeight, w = innerWidth;
    let lift = 0;
    [0.2, 0.5, 0.8].forEach((x) => {
      const el = document.elementsFromPoint(w * x, h - 3).find((n) => !toast.contains(n));
      for (let n = el; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
        if (getComputedStyle(n).position !== 'fixed') continue;
        const r = n.getBoundingClientRect();
        if (r.height < h * 0.35 && r.width > w * 0.4 && r.bottom >= h - 6) lift = Math.max(lift, Math.round(h - r.top));
        break;
      }
    });
    return lift;
  };

  const showToast = (card, isError, message) => {
    const t = getToast();
    const img = card && card.querySelector('.dvf-card__media img');
    const title = card && card.querySelector('.dvf-card__title');
    const vars = card ? getComputedStyle(card) : null;
    if (vars) ['--dvf-btn', '--dvf-accent', '--dvf-font'].forEach((v) => t.style.setProperty(v, vars.getPropertyValue(v)));
    t.style.setProperty('--dvf-lift', bottomBarHeight() + 'px');

    t.classList.toggle('is-error', isError);
    if (img && !isError) t.querySelector('img').src = img.currentSrc || img.src;
    else t.querySelector('img').removeAttribute('src');
    t.querySelector('b').textContent = isError ? 'Not added' : 'Added to cart';
    t.querySelector('span').textContent = isError ? message : (title ? title.textContent.trim() : '');
    t.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('is-on'), isError ? 5000 : 4000);
  };

  /* Hide the toast when the cart drawer or a menu opens on top of it */
  document.addEventListener('cart:refresh', (e) => {
    if (e.detail && e.detail.openMiniCart && toast) toast.classList.remove('is-on');
  });

  window.DVFCard = { timeLeft };
})();
